import React from 'react';
import * as Sentry from '@sentry/browser';
import { WithPermissions } from 'react-admin';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import { Typography } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import PropTypes from 'prop-types';
import config from '../../../../../../../setup/config';
import { getExcel, getExcelDashboard } from '../../../../services/excelService';
import ExcelImage from '../../../../../../../assets/images/excel.png';
import PhotoImage from '../../../../../../../assets/images/photos.png';
import DownloadImage from '../../../../../../../assets/images/downloadGallery.png';
import FullScreenImage from '../../../../../../../assets/images/wideScreen.png';
import DashboardImage from '../../../../../../../assets/images/dashboard.png';
import GoBackButton from './goBackButton';
import isAbsentOrBlank from '../../../../../../../util/stringUtil';

const styles = {
  cardActionStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  progress: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '10px',
    alignItems: 'center',
  },
  iconDownloadCampaign: {
    top: '3px',
  },
  downloadCampaign: {
    color: '#03a6e9',
  },
  logo: {
    maxHeight: '20px',
  },
  error: {
    textAlign: 'center',
    border: '1px solid red',
    padding: '10px',
    color: 'red',
  },
};

const tooltip = {
  fullscreen: 'Open photo gallery - full screen',
  halfscreen: 'Open photo gallery - half screen',
  excellDownload: 'Download raw data (.xlsx)',
  downloadCampaign: 'Download photo gallery (.zip)',
  dashboardExport: 'Download charts (.xlsx)',
};

export class Title extends React.PureComponent {
  state = {
    isExcelLoading: false,
    excelLoadingError: undefined,
  };

  extractErrorClassName = fullyQualifiedName => {
    const nullSafeInput = fullyQualifiedName || '';
    const lastIndexOfDot = nullSafeInput.lastIndexOf('.');
    return lastIndexOfDot < 0
      ? nullSafeInput
      : nullSafeInput.substring(lastIndexOfDot + 1, nullSafeInput.length);
  };

  downloadExcell = () => {
    this.setState({ isExcelLoading: true, excelLoadingError: undefined }, async () => {
      try {
        const result = await getExcel(this.props.record.id);
        this.setState({
          excelLoadingError: result
            ? {
                type: this.extractErrorClassName((result || {}).type || 'UnknownError'),
                message:
                  (result || {}).message ||
                  'Excel file download error, please try again later or contact us if it persists.',
              }
            : undefined,
          isExcelLoading: false,
        });
      } catch (e) {
        Sentry.captureException(e);
        this.setState({
          excelLoadingError: {
            type: 'UnhandledError',
            message: `Unhandled error when downloading file. Detail: ${e}`,
          },
          isExcelLoading: false,
        });
      }
    });
  };

  downloadDashboard = () => {
    this.setState({ isExcelLoading: true }, async () => {
      try {
        await getExcelDashboard(this.props.record.id);
        this.setState({
          isExcelLoading: false,
        });
      } catch (e) {
        Sentry.captureException(e);
        this.setState({
          excelLoadingError: {
            type: 'UnhandledError',
            message: `Unhandled error when downloading file. Detail: ${e}`,
          },
          isExcelLoading: false,
        });
      }
    });
  };

  renderDownloadCampaign = () => (
    <Tooltip title={tooltip.downloadCampaign}>
      <IconButton style={styles.iconDownloadCampaign}>
        <a
          href={`${config.FAN_BACKEND_BASE_URL}/campaigns/${
            this.props.record.id
          }/photos?parse-session=${localStorage.getItem('token')}`}
          download
        >
          <img testid="downloadCampaignButton" src={DownloadImage} alt="" style={styles.logo} />
        </a>
      </IconButton>
    </Tooltip>
  );

  renderPhotogalleryDrawers = () => (
    <React.Fragment>
      <Tooltip title={tooltip.halfscreen}>
        <IconButton onClick={() => this.props.onToggleViewGallery('50vh')}>
          <img testid="halfscreen" src={PhotoImage} alt="" style={styles.logo} />
        </IconButton>
      </Tooltip>
      <Tooltip title={tooltip.fullscreen}>
        <IconButton
          testid="fullscreenButton"
          onClick={() => this.props.onToggleViewGallery('calc(100vh - 63px)')}
        >
          <img testid="fullscreen" src={FullScreenImage} alt="" style={styles.logo} />
        </IconButton>
      </Tooltip>
    </React.Fragment>
  );

  renderPhotoGalleryIcons() {
    const { isGalleryLoaded, isGalleryNotEmpty, record } = this.props;
    if (!isAbsentOrBlank(record.metabaseId) || !isAbsentOrBlank(record.metabaseDashboardUrl)) {
      return (
        <React.Fragment>
          {this.renderDownloadCampaign()}
          {this.renderPhotogalleryDrawers()}
        </React.Fragment>
      );
    } else if (record.metabaseId === undefined && isGalleryNotEmpty === false && isGalleryLoaded) {
      return null;
    }
    return this.renderDownloadCampaign();
  }

  renderExportDashboard = () => {
    const { record } = this.props;
    if (
      (!isAbsentOrBlank(record.metabaseId) && isAbsentOrBlank(record.metabaseDashboardUrl)) ||
      (!isAbsentOrBlank(record.metabaseDashboardUrl) &&
        record.metabaseDashboardUrl.includes('.snooper-app.com'))
    ) {
      return (
        <React.Fragment>
          <Tooltip title={tooltip.dashboardExport}>
            <IconButton testid="exportDashboard" onClick={this.downloadDashboard}>
              <img src={DashboardImage} alt="" style={styles.logo} />
            </IconButton>
          </Tooltip>
        </React.Fragment>
      );
    }
    return null;
  };

  render() {
    const { record, permissions } = this.props;
    const { isExcelLoading, excelLoadingError } = this.state;

    const hasExcelLoadingError = !!excelLoadingError;
    if (isExcelLoading) {
      return (
        <div style={styles.progress}>
          <CircularProgress testid="excelProgress" />
          <Typography>Excel file downloading</Typography>
        </div>
      );
    }

    return (
      <React.Fragment>
        <div style={styles.cardActionStyle}>
          <div>
            <GoBackButton />
            <span testid="title"> {record.name ? `${record.name}` : ''}</span>
          </div>

          <div testid="galleryIcons">
            <Tooltip title={tooltip.excellDownload}>
              <IconButton testid="excelIconButton" onClick={this.downloadExcell}>
                <img src={ExcelImage} alt="" style={styles.logo} />
              </IconButton>
            </Tooltip>
            {permissions ? this.renderExportDashboard() : null}
            {this.renderPhotoGalleryIcons()}
          </div>
        </div>
        {hasExcelLoadingError ? (
          <div style={styles.error}>
            <Typography testid="errorLoadingExcel">
              {excelLoadingError.type}
              <br />
              {excelLoadingError.message}
            </Typography>
          </div>
        ) : null}
      </React.Fragment>
    );
  }
}

Title.propTypes = {
  record: PropTypes.objectOf(PropTypes.any),
  onToggleViewGallery: PropTypes.func.isRequired,
  isGalleryLoaded: PropTypes.bool.isRequired,
  isGalleryNotEmpty: PropTypes.bool.isRequired,
  permissions: PropTypes.bool,
};

Title.defaultProps = {
  record: null,
  permissions: false,
};

const TitleExport = props => (
  <WithPermissions render={({ permissions }) => <Title {...props} permissions={permissions} />} />
);

export default TitleExport;
